import type {
  LscAutoVerifyEligibilityCandidate,
  LscAutoVerifyEligibilityReport,
  LscVodCandidate,
} from './types';
import { IGNORE_VOD_REASON_OPTIONS } from './commissionerReviewCopy';
import {
  candidateHasSyncProvenance,
  getVodReviewQueueBucket,
  isVerifiedLiveHandoffCandidate,
  isVodReviewMetadataOnlyUnmatched,
  VERIFIED_LIVE_HANDOFF_MATCH_SOURCE,
} from './vodTruth';

export type ArchiveBackfillBucket =
  | 'verified_live_handoff'
  | 'likely_schedule_match'
  | 'needs_match'
  | 'identity_unclear'
  | 'suggested_dismiss_later'
  | 'already_handled';

export const ARCHIVE_BACKFILL_BUCKET_ORDER: ArchiveBackfillBucket[] = [
  'verified_live_handoff',
  'likely_schedule_match',
  'needs_match',
  'identity_unclear',
  'suggested_dismiss_later',
  'already_handled',
];

export const ARCHIVE_BACKFILL_BUCKET_LABELS: Record<ArchiveBackfillBucket, string> = {
  verified_live_handoff: 'High-confidence verified handoffs',
  likely_schedule_match: 'Likely schedule matches',
  needs_match: 'Needs match',
  identity_unclear: 'Identity unclear',
  suggested_dismiss_later: 'Suggested dismiss later',
  already_handled: 'Already handled',
};

export const ARCHIVE_BACKFILL_BUCKET_DESCRIPTIONS: Record<ArchiveBackfillBucket, string> = {
  verified_live_handoff:
    'Verified live handoff. LSC verified this stream live and linked the Twitch VOD automatically.',
  likely_schedule_match:
    'Likely scheduled game. Confirm the match and evidence before approval.',
  needs_match: 'Synced VOD needs a scheduled game assignment.',
  identity_unclear: 'Broadcaster identity needs commissioner review.',
  suggested_dismiss_later:
    'Likely cleanup candidate. Open review before taking action.',
  already_handled: 'Already handled or closed.',
};

export const ARCHIVE_BACKFILL_DISPLAY_LIMIT = 10;
export const ARCHIVE_BACKFILL_BUCKET_INCREMENT = 10;

export const ARCHIVE_BACKFILL_BUCKET_INITIAL_LIMIT = ARCHIVE_BACKFILL_DISPLAY_LIMIT;

export const ARCHIVE_BACKFILL_MIN_DURATION_SECONDS = 600;
export const ARCHIVE_BACKFILL_MAX_VOD_AGE_DAYS = 14;

const LEGACY_SYNC_SOURCES = new Set([
  'manual_sync',
  'scheduled_sync',
  'admin_sweep',
  'vod_handoff_automation',
]);

const LIKELY_MATCH_ELIGIBILITY_CLASSES = new Set([
  'eligible_commissioner_matched',
  'eligible_matcher_suggested_high_confidence',
  'eligible_matcher_suggested_medium_confidence',
]);

const SUGGESTED_DISMISS_ELIGIBILITY_CLASSES = new Set([
  'blocked_archive_duplicate',
  'blocked_terminal',
]);

const IDENTITY_ELIGIBILITY_CLASSES = new Set(['blocked_identity']);

const NEEDS_MATCH_ELIGIBILITY_CLASSES = new Set(['ineligible_metadata_unmatched']);

export { isVerifiedLiveHandoffCandidate };

export function isLegacySyncCandidate(candidate: LscVodCandidate): boolean {
  if (isVerifiedLiveHandoffCandidate(candidate)) {
    return false;
  }
  if (candidateHasSyncProvenance(candidate)) {
    return true;
  }
  const syncSource = String(candidate.sync_source ?? '').trim();
  if (syncSource && LEGACY_SYNC_SOURCES.has(syncSource)) {
    return true;
  }
  const lastSync = String(candidate.source_metadata?.last_sync_source ?? '').trim();
  return Boolean(lastSync);
}

export function buildEligibilityByCandidateId(
  report?: LscAutoVerifyEligibilityReport | null
): Map<string, LscAutoVerifyEligibilityCandidate> {
  const map = new Map<string, LscAutoVerifyEligibilityCandidate>();
  for (const entry of report?.candidates ?? []) {
    const candidateId = String(entry.candidate_id ?? '').trim();
    if (candidateId) {
      map.set(candidateId, entry);
    }
  }
  return map;
}

function eligibilityForCandidate(
  candidate: LscVodCandidate,
  eligibilityById?: Map<string, LscAutoVerifyEligibilityCandidate>
): LscAutoVerifyEligibilityCandidate | undefined {
  const candidateId = String(candidate.id ?? '').trim();
  if (!candidateId || !eligibilityById) {
    return undefined;
  }
  return eligibilityById.get(candidateId);
}

export function isArchiveBackfillAlreadyHandled(candidate: LscVodCandidate): boolean {
  const reviewStatus = String(candidate.review_status ?? 'pending_review').trim().toLowerCase();
  if (reviewStatus === 'published' || reviewStatus === 'rejected') {
    return true;
  }
  const candidateStatus = String(candidate.candidate_status ?? 'candidate').trim().toLowerCase();
  if (candidateStatus === 'ignored' || candidateStatus === 'expired' || candidateStatus === 'unavailable') {
    return true;
  }
  const archiveStatus = String(candidate.archive_status ?? '').trim().toLowerCase();
  return archiveStatus === 'published' || archiveStatus === 'removed';
}

function hasArchiveDuplicateSignal(
  candidate: LscVodCandidate,
  eligibility?: LscAutoVerifyEligibilityCandidate
): boolean {
  if (eligibility?.eligibility_class === 'blocked_archive_duplicate') {
    return true;
  }
  return (eligibility?.blockers ?? []).some((blocker) =>
    String(blocker).toLowerCase().includes('archive_duplicate')
  );
}

function hasStaleOrShortSignal(
  candidate: LscVodCandidate,
  eligibility?: LscAutoVerifyEligibilityCandidate
): boolean {
  const blockers = eligibility?.blockers ?? [];
  if (
    blockers.some(
      (blocker) =>
        String(blocker).includes('duration_seconds_below_min') ||
        String(blocker).includes('vod_age_exceeds_max')
    )
  ) {
    return true;
  }
  const duration = candidate.duration_seconds;
  if (duration != null && duration < ARCHIVE_BACKFILL_MIN_DURATION_SECONDS) {
    return true;
  }
  if (candidate.competition_match_id) {
    return false;
  }
  const touch = candidate.updated_at ?? candidate.created_at ?? candidate.published_at;
  if (touch) {
    const ageMs = Date.now() - Date.parse(touch);
    if (!Number.isNaN(ageMs)) {
      const ageDays = ageMs / (1000 * 60 * 60 * 24);
      if (ageDays > ARCHIVE_BACKFILL_MAX_VOD_AGE_DAYS) {
        return true;
      }
    }
  }
  return false;
}

export function isArchiveBackfillSuggestedDismissLater(
  candidate: LscVodCandidate,
  eligibility?: LscAutoVerifyEligibilityCandidate
): boolean {
  if (isVerifiedLiveHandoffCandidate(candidate)) {
    return false;
  }
  if (isArchiveBackfillAlreadyHandled(candidate)) {
    return false;
  }
  if (candidate.competition_match_id) {
    return false;
  }
  if (isVodReviewMetadataOnlyUnmatched(candidate)) {
    return false;
  }
  if (eligibility?.eligibility_class && SUGGESTED_DISMISS_ELIGIBILITY_CLASSES.has(eligibility.eligibility_class)) {
    return true;
  }
  if (hasArchiveDuplicateSignal(candidate, eligibility)) {
    return true;
  }
  if (hasStaleOrShortSignal(candidate, eligibility)) {
    return true;
  }
  const resolution = String(candidate.truth_league_match_resolution ?? '').trim().toLowerCase();
  if (resolution === 'no_league_match' && !candidate.competition_match_id && isLegacySyncCandidate(candidate)) {
    return true;
  }
  return false;
}

export function isArchiveBackfillIdentityUnclear(
  candidate: LscVodCandidate,
  eligibility?: LscAutoVerifyEligibilityCandidate
): boolean {
  if (isArchiveBackfillAlreadyHandled(candidate)) {
    return false;
  }
  if (getVodReviewQueueBucket(candidate) === 'needs_identity') {
    return true;
  }
  if (eligibility?.eligibility_class && IDENTITY_ELIGIBILITY_CLASSES.has(eligibility.eligibility_class)) {
    return true;
  }
  const resolution = String(candidate.truth_league_match_resolution ?? '').trim().toLowerCase();
  return resolution === 'cross_league_identity_conflict';
}

export function isArchiveBackfillNeedsMatch(
  candidate: LscVodCandidate,
  eligibility?: LscAutoVerifyEligibilityCandidate
): boolean {
  if (isArchiveBackfillAlreadyHandled(candidate)) {
    return false;
  }
  if (isVerifiedLiveHandoffCandidate(candidate)) {
    return false;
  }
  if (isVodReviewMetadataOnlyUnmatched(candidate)) {
    return true;
  }
  if (eligibility?.eligibility_class && NEEDS_MATCH_ELIGIBILITY_CLASSES.has(eligibility.eligibility_class)) {
    return true;
  }
  if (!candidate.competition_match_id) {
    const matchStatus = String(candidate.match_status ?? '').trim().toLowerCase();
    if (matchStatus === 'unmatched' || matchStatus === 'no_match') {
      return true;
    }
  }
  if (getVodReviewQueueBucket(candidate) === 'needs_match') {
    return true;
  }
  return false;
}

export function isArchiveBackfillLikelyScheduleMatch(
  candidate: LscVodCandidate,
  eligibility?: LscAutoVerifyEligibilityCandidate
): boolean {
  if (isVerifiedLiveHandoffCandidate(candidate)) {
    return false;
  }
  if (candidate.competition_match_id) {
    return true;
  }
  const matchStatus = String(candidate.match_status ?? '').trim().toLowerCase();
  if (matchStatus === 'suggested') {
    return true;
  }
  if (eligibility?.eligibility_class && LIKELY_MATCH_ELIGIBILITY_CLASSES.has(eligibility.eligibility_class)) {
    return true;
  }
  return false;
}

export function classifyArchiveBackfillCandidate(
  candidate: LscVodCandidate,
  eligibilityReport?: LscAutoVerifyEligibilityReport | null
): ArchiveBackfillBucket {
  const eligibilityById = buildEligibilityByCandidateId(eligibilityReport);
  const eligibility = eligibilityForCandidate(candidate, eligibilityById);

  if (isArchiveBackfillAlreadyHandled(candidate)) {
    return 'already_handled';
  }
  if (isVerifiedLiveHandoffCandidate(candidate)) {
    return 'verified_live_handoff';
  }
  if (isArchiveBackfillSuggestedDismissLater(candidate, eligibility)) {
    return 'suggested_dismiss_later';
  }
  if (isArchiveBackfillIdentityUnclear(candidate, eligibility)) {
    return 'identity_unclear';
  }
  if (isArchiveBackfillNeedsMatch(candidate, eligibility)) {
    return 'needs_match';
  }
  if (isArchiveBackfillLikelyScheduleMatch(candidate, eligibility)) {
    return 'likely_schedule_match';
  }
  return 'needs_match';
}

export function getArchiveBackfillBucketLabel(bucket: ArchiveBackfillBucket): string {
  return ARCHIVE_BACKFILL_BUCKET_LABELS[bucket];
}

export function getArchiveBackfillReason(
  candidate: LscVodCandidate,
  eligibilityReport?: LscAutoVerifyEligibilityReport | null
): string {
  const bucket = classifyArchiveBackfillCandidate(candidate, eligibilityReport);
  const eligibility = eligibilityForCandidate(
    candidate,
    buildEligibilityByCandidateId(eligibilityReport)
  );
  const eligibilityReason = eligibility?.reasons?.[0];
  if (eligibilityReason) {
    return eligibilityReason.replace(/_/g, ' ');
  }
  if (bucket === 'verified_live_handoff') {
    const streamId = candidate.source_metadata?.stream_id;
    if (streamId) {
      return `Linked from live stream ${String(streamId)} via verified handoff.`;
    }
    return ARCHIVE_BACKFILL_BUCKET_DESCRIPTIONS.verified_live_handoff;
  }
  if (bucket === 'likely_schedule_match' && candidate.competition_match_id) {
    return `Match assigned (${candidate.competition_match_id}). Confirm evidence before approval.`;
  }
  if (bucket === 'needs_match' && isVodReviewMetadataOnlyUnmatched(candidate)) {
    return 'Synced Twitch metadata only — no schedule match or frame verification yet.';
  }
  if (bucket === 'identity_unclear') {
    return candidate.truth_league_resolution_summary?.trim() || ARCHIVE_BACKFILL_BUCKET_DESCRIPTIONS.identity_unclear;
  }
  if (bucket === 'suggested_dismiss_later') {
    const blocker = eligibility?.blockers?.[0];
    if (blocker) {
      return `Cleanup signal: ${blocker.replace(/_/g, ' ')}.`;
    }
    return ARCHIVE_BACKFILL_BUCKET_DESCRIPTIONS.suggested_dismiss_later;
  }
  return ARCHIVE_BACKFILL_BUCKET_DESCRIPTIONS[bucket];
}

export function getArchiveBackfillRecommendedAction(bucket: ArchiveBackfillBucket): string {
  if (bucket === 'already_handled') {
    return 'View details';
  }
  return 'Open review';
}

export function sortArchiveBackfillCandidates(candidates: LscVodCandidate[]): LscVodCandidate[] {
  return [...candidates].sort((left, right) => {
    const leftHandoff = isVerifiedLiveHandoffCandidate(left) ? 1 : 0;
    const rightHandoff = isVerifiedLiveHandoffCandidate(right) ? 1 : 0;
    if (leftHandoff !== rightHandoff) {
      return rightHandoff - leftHandoff;
    }
    const leftMatch = left.competition_match_id ? 1 : 0;
    const rightMatch = right.competition_match_id ? 1 : 0;
    if (leftMatch !== rightMatch) {
      return rightMatch - leftMatch;
    }
    const leftTime = Date.parse(left.published_at ?? left.created_at ?? left.updated_at ?? '') || 0;
    const rightTime = Date.parse(right.published_at ?? right.created_at ?? right.updated_at ?? '') || 0;
    return rightTime - leftTime;
  });
}

export function groupArchiveBackfillCandidatesByBucket(
  candidates: LscVodCandidate[],
  eligibilityReport?: LscAutoVerifyEligibilityReport | null
): Record<ArchiveBackfillBucket, LscVodCandidate[]> {
  const groups = ARCHIVE_BACKFILL_BUCKET_ORDER.reduce(
    (acc, bucket) => {
      acc[bucket] = [];
      return acc;
    },
    {} as Record<ArchiveBackfillBucket, LscVodCandidate[]>
  );
  for (const candidate of candidates) {
    const bucket = classifyArchiveBackfillCandidate(candidate, eligibilityReport);
    groups[bucket].push(candidate);
  }
  for (const bucket of ARCHIVE_BACKFILL_BUCKET_ORDER) {
    groups[bucket] = sortArchiveBackfillCandidates(groups[bucket]);
  }
  return groups;
}

export function summarizeArchiveBackfillBuckets(
  candidates: LscVodCandidate[],
  eligibilityReport?: LscAutoVerifyEligibilityReport | null
): Record<ArchiveBackfillBucket, number> {
  const groups = groupArchiveBackfillCandidatesByBucket(candidates, eligibilityReport);
  return ARCHIVE_BACKFILL_BUCKET_ORDER.reduce(
    (counts, bucket) => {
      counts[bucket] = groups[bucket].length;
      return counts;
    },
    {} as Record<ArchiveBackfillBucket, number>
  );
}

export function formatArchiveBackfillMatchSource(candidate: LscVodCandidate): string {
  const matchSource = String(candidate.match_source ?? '').trim();
  if (matchSource === VERIFIED_LIVE_HANDOFF_MATCH_SOURCE) {
    return 'Verified live handoff';
  }
  if (matchSource) {
    return matchSource.replace(/_/g, ' ');
  }
  return '—';
}

export function formatArchiveBackfillCandidateSource(candidate: LscVodCandidate): string {
  const syncSource = String(candidate.sync_source ?? candidate.source_metadata?.last_sync_source ?? '').trim();
  if (syncSource) {
    return syncSource.replace(/_/g, ' ');
  }
  if (isVerifiedLiveHandoffCandidate(candidate)) {
    return 'verified live handoff';
  }
  return '—';
}

const IGNORE_ELIGIBLE_BACKFILL_BUCKETS = new Set<ArchiveBackfillBucket>([
  'needs_match',
  'suggested_dismiss_later',
]);

const VOD_HANDOFF_AUTOMATION_SYNC_SOURCE = 'vod_handoff_automation';

export function canShowIgnoreCandidateAction(
  candidate: LscVodCandidate,
  bucket: ArchiveBackfillBucket
): boolean {
  if (!IGNORE_ELIGIBLE_BACKFILL_BUCKETS.has(bucket)) {
    return false;
  }
  if (isArchiveBackfillAlreadyHandled(candidate)) {
    return false;
  }
  if (isVerifiedLiveHandoffCandidate(candidate)) {
    return false;
  }
  const candidateStatus = String(candidate.candidate_status ?? 'candidate').trim().toLowerCase();
  if (candidateStatus !== 'candidate') {
    return false;
  }
  const reviewStatus = String(candidate.review_status ?? 'pending_review').trim().toLowerCase();
  if (reviewStatus === 'approved' || reviewStatus === 'published' || reviewStatus === 'rejected') {
    return false;
  }
  const archiveStatus = String(candidate.archive_status ?? '').trim().toLowerCase();
  if (archiveStatus === 'published') {
    return false;
  }
  const matchSource = String(candidate.match_source ?? '').trim();
  if (matchSource === VERIFIED_LIVE_HANDOFF_MATCH_SOURCE) {
    return false;
  }
  const syncSource = String(candidate.sync_source ?? '').trim();
  if (syncSource === VOD_HANDOFF_AUTOMATION_SYNC_SOURCE) {
    return false;
  }
  return true;
}

export type CommissionerIgnoreMetadata = {
  reason?: string;
  note?: string;
  ignored_at?: string;
  actor_uid?: string;
};

export function isIgnoredVodCandidate(
  candidate: Pick<LscVodCandidate, 'candidate_status'>
): boolean {
  return String(candidate.candidate_status ?? 'candidate').trim().toLowerCase() === 'ignored';
}

export function filterActiveReviewQueueCandidates(candidates: LscVodCandidate[]): LscVodCandidate[] {
  return candidates.filter((candidate) => !isIgnoredVodCandidate(candidate));
}

export function formatIgnoreReasonLabel(reason?: string | null): string {
  const normalized = String(reason ?? '').trim();
  if (!normalized) {
    return '—';
  }
  const match = IGNORE_VOD_REASON_OPTIONS.find((option) => option.value === normalized);
  if (match) {
    return match.label;
  }
  return normalized.replace(/_/g, ' ');
}

export function getCommissionerIgnoreMetadata(
  candidate: LscVodCandidate
): CommissionerIgnoreMetadata | null {
  const raw = candidate.source_metadata?.commissioner_ignore;
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  return {
    reason: typeof record.reason === 'string' ? record.reason : undefined,
    note: typeof record.note === 'string' ? record.note : undefined,
    ignored_at: typeof record.ignored_at === 'string' ? record.ignored_at : undefined,
    actor_uid: typeof record.actor_uid === 'string' ? record.actor_uid : undefined,
  };
}

export function normalizeArchiveBackfillSearchText(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

export function matchesArchiveBackfillSearchQuery(
  candidate: LscVodCandidate,
  query: string
): boolean {
  const normalizedQuery = normalizeArchiveBackfillSearchText(query);
  if (!normalizedQuery) {
    return true;
  }
  const haystack = [
    candidate.id,
    candidate.provider_vod_id,
    candidate.title,
    candidate.provider_broadcaster_login,
    candidate.provider_broadcaster_display_name,
  ]
    .map(normalizeArchiveBackfillSearchText)
    .join(' ');
  return haystack.includes(normalizedQuery);
}

export function filterArchiveBackfillCandidates(
  candidates: LscVodCandidate[],
  query: string
): LscVodCandidate[] {
  if (!normalizeArchiveBackfillSearchText(query)) {
    return candidates;
  }
  return candidates.filter((candidate) => matchesArchiveBackfillSearchQuery(candidate, query));
}

export function formatIgnoredAtLabel(value?: string | null): string | null {
  if (!value?.trim()) {
    return null;
  }
  try {
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}
